import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { MoreHorizontal, Edit, Trash2, Link, MessageCircle, FileText, Folder, ChevronDown, ChevronRight } from "lucide-react";
import { chatThreadsService } from "@/services/chatThreadsService";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";
import { useDynamicTruncate } from "@/hooks/use-dynamic-truncate";

function truncateTitle(title: string, maxLength: number): string {
  if (!title || title.length <= maxLength) return title;
  return title.slice(0, maxLength) + "...";
}

/** Single thread row in TopicCard list accordion; uses width observer for responsive title length */
function TopicCardThreadRow({ thread, onThreadClick }: { thread: { id: string; title?: string | null; updatedAt?: string | null; tags?: string[] }; onThreadClick: (id: string) => void }) {
  const { maxLength, containerRef } = useDynamicTruncate({
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    minChars: 10,
    maxChars: 120,
    capacityFactor: 0.98,
  });
  const title = thread.title || "Untitled Thread";
  return (
    <div
      className="flex items-center gap-3 p-2 rounded-md hover:bg-background cursor-pointer transition-colors"
      onClick={() => onThreadClick(thread.id)}
    >
      <MessageCircle className="h-4 w-4 text-muted-foreground shrink-0" />
      <div ref={containerRef} className="flex-1 min-w-0 overflow-hidden">
        <div className="text-sm font-medium truncate" title={title}>
          {truncateTitle(title, maxLength)}
        </div>
        <div className="text-xs text-muted-foreground">
          {thread.updatedAt
            ? formatDistanceToNow(new Date(thread.updatedAt), { addSuffix: true })
            : "No date"}
        </div>
      </div>
      {thread.tags && thread.tags.length > 0 && (
        <div className="flex gap-1">
          {thread.tags.slice(0, 2).map((tag: string, idx: number) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

interface TopicCardProps {
  topic: {
    id: string;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    spaceId: string;
    primarySpace?: { id: string; name: string; icon?: string; color?: string } | null;
    secondarySpaces?: Array<{ id: string; name: string; icon?: string; color?: string }>;
    threadCount?: number;
    sourceCount?: number;
    tags?: string[];
  };
  onEdit?: (topic: any) => void;
  onDelete?: (topic: any) => void;
  onLinkSpace?: (topic: any) => void;
  onClick?: (topic: any) => void;
  viewMode?: "grid" | "list";
}

export function TopicCard({ topic, onEdit, onDelete, onLinkSpace, onClick, viewMode = "grid" }: TopicCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [, setLocation] = useLocation();

  const { data: threads = [], isLoading: threadsLoading } = useQuery({
    queryKey: ["/api/chat-threads/by-topic", topic.id],
    queryFn: async () => {
      return await chatThreadsService.getThreadSummariesByTopic(topic.id);
    },
    enabled: isExpanded && viewMode === "list",
  });

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-action]")) return;
    onClick?.(topic);
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleThreadClick = (threadId: string) => {
    setLocation(`/chat?threadId=${threadId}`);
  };

  if (viewMode === "list") {
    return (
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div
          className="flex items-center justify-between p-4 border-b hover:bg-muted/50 cursor-pointer"
          data-testid={`topic-row-${topic.id}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 shrink-0"
                onClick={handleToggleExpand}
                data-action="expand"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <div className="text-2xl shrink-0" data-testid={`topic-icon-${topic.id}`}>
              {topic.icon || <Folder className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="min-w-0 flex-1" onClick={handleCardClick}>
              <h3 className="font-medium truncate" data-testid={`topic-name-${topic.id}`}>{topic.name}</h3>
              {topic.description && (
                <p className="text-sm text-muted-foreground truncate">{topic.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {topic.primarySpace && (
              <Badge variant="secondary" className="shrink-0" data-testid={`topic-primary-space-${topic.id}`}>
                {topic.primarySpace.icon} {topic.primarySpace.name}
              </Badge>
            )}
            
            {topic.secondarySpaces && topic.secondarySpaces.length > 0 && (
              <div className="flex gap-1">
                {topic.secondarySpaces.slice(0, 2).map((space) => (
                  <Badge key={space.id} variant="outline" className="text-xs" data-testid={`topic-secondary-space-${topic.id}-${space.id}`}>
                    {space.icon} {space.name}
                  </Badge>
                ))}
                {topic.secondarySpaces.length > 2 && (
                  <Badge variant="outline" className="text-xs">+{topic.secondarySpaces.length - 2}</Badge>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1" data-testid={`topic-thread-count-${topic.id}`}>
                <MessageCircle className="h-4 w-4" /> {topic.threadCount || 0}
              </span>
              <span className="flex items-center gap-1" data-testid={`topic-source-count-${topic.id}`}>
                <FileText className="h-4 w-4" /> {topic.sourceCount || 0}
              </span>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild data-action="menu">
                <Button variant="ghost" size="icon" data-testid={`topic-menu-${topic.id}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit?.(topic)} data-testid={`topic-edit-${topic.id}`}>
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onLinkSpace?.(topic)} data-testid={`topic-link-space-${topic.id}`}>
                  <Link className="h-4 w-4 mr-2" /> Link to Space
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete?.(topic)} className="text-destructive" data-testid={`topic-delete-${topic.id}`}>
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <CollapsibleContent>
          <div className="pl-14 pr-4 pb-4 bg-muted/30 border-b">
            {threadsLoading ? (
              <div className="py-3 text-sm text-muted-foreground">Loading threads...</div>
            ) : threads.length === 0 ? (
              <div className="py-3 text-sm text-muted-foreground italic">No threads linked to this topic</div>
            ) : (
              <div className="space-y-1 py-2">
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  {threads.length} Thread{threads.length !== 1 ? "s" : ""}
                </div>
                {threads.map((thread: any) => (
                  <TopicCardThreadRow
                    key={thread.id}
                    thread={thread}
                    onThreadClick={handleThreadClick}
                  />
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Card 
      className="hover-elevate cursor-pointer transition-shadow"
      onClick={handleCardClick}
      data-testid={`topic-card-${topic.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="text-3xl" data-testid={`topic-icon-${topic.id}`}>
            {topic.icon || <Folder className="h-8 w-8 text-muted-foreground" />}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild data-action="menu">
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`topic-menu-${topic.id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit?.(topic)} data-testid={`topic-edit-${topic.id}`}>
                <Edit className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onLinkSpace?.(topic)} data-testid={`topic-link-space-${topic.id}`}>
                <Link className="h-4 w-4 mr-2" /> Link to Space
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete?.(topic)} className="text-destructive" data-testid={`topic-delete-${topic.id}`}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <h3 className="font-semibold text-lg mb-1 line-clamp-1" data-testid={`topic-name-${topic.id}`}>
          {topic.name}
        </h3>
        {topic.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{topic.description}</p>
        )}

        <div className="space-y-2">
          {topic.primarySpace && (
            <Badge variant="secondary" className="mr-1" data-testid={`topic-primary-space-${topic.id}`}>
              {topic.primarySpace.icon} {topic.primarySpace.name}
            </Badge>
          )}
          
          {topic.secondarySpaces && topic.secondarySpaces.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {topic.secondarySpaces.slice(0, 3).map((space) => (
                <Badge key={space.id} variant="outline" className="text-xs" data-testid={`topic-secondary-space-${topic.id}-${space.id}`}>
                  {space.icon} {space.name}
                </Badge>
              ))}
              {topic.secondarySpaces.length > 3 && (
                <Badge variant="outline" className="text-xs">+{topic.secondarySpaces.length - 3}</Badge>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t text-sm text-muted-foreground">
          <span className="flex items-center gap-1" data-testid={`topic-thread-count-${topic.id}`}>
            <MessageCircle className="h-4 w-4" /> {topic.threadCount || 0} threads
          </span>
          <span className="flex items-center gap-1" data-testid={`topic-source-count-${topic.id}`}>
            <FileText className="h-4 w-4" /> {topic.sourceCount || 0} sources
          </span>
        </div>

        {topic.tags && topic.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {topic.tags.slice(0, 4).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
